/**
 * LMDR Commerce runtime — entry point.
 *
 * Drop-in replacement pour `cdn.smootify.io/assets/latest/js/index.js`.
 * Expose la même surface publique que Smootify (events, window.Smootify).
 *
 * Bootstrap :
 *   1. Load config depuis window.SmootifyUserOptions
 *   2. Register Web Components (<smootify-product>, <wishlist-toggle>)
 *   3. Hydrate le panier depuis localStorage + Storefront
 *   4. Scanner global : attach handlers sur [action=...], hydrate cart indicators
 *   5. Installer window.Smootify API
 *   6. Dispatch smootify:loaded
 *   7. Exécuter les <script type="smootify-load">
 */

import { config, ensureConfigReady } from "./config.js";
import { bootstrapLocale } from "./i18n/locale.js";
import { attachConsentListener } from "./i18n/consent.js";
import { attachConsentMode } from "./i18n/consent-mode.js";
import { attachAnalytics } from "./i18n/shopify-analytics.js";
import { attachCookieBanner } from "./i18n/cookie-banner.js";
import { attachWishlistStorage } from "./elements/wishlist-toggle.js";
import { attachWishlistBadge } from "./ui/wishlist-badge.js";
import { registerElements } from "./elements/register.js";
import { attachActionHandlers } from "./attributes/action.js";
import { attachCartAutoRefresh, attachCartTriggers } from "./attributes/cart.js";
import { attachFreeShipping } from "./attributes/free-shipping.js";
import { attachCountdown } from "./attributes/countdown.js";
import { attachDraftCart } from "./attributes/draft-cart.js";
import { ensureHiddenStyle, attachConditionAutoRefresh } from "./attributes/condition.js";
import { loadCart } from "./cart/state.js";
import { installSmootifyApi } from "./api/public.js";
import { emit, setupSmootifyLoadScripts } from "./events/bus.js";
import { injectCriticalStyle } from "./ui/critical-style.js";
import { refreshFilters } from "./attributes/filter.js";
import { attachPredictiveSearch } from "./attributes/predictive-search.js";

// ---------------------------------------------------------------------------
// Anti-FOUC — injecté IMMÉDIATEMENT au parse du module, avant même que
// `boot()` ne soit appelé (boot attend DOMContentLoaded). Objectif : cacher
// les templates Webflow (wishlist/related/last-viewed/cart-upsells), les
// [condition] non résolus et les placeholders d'option de variante avant
// le premier paint, pour éviter le flash du placeholder "Heading/15.99$".
// ---------------------------------------------------------------------------
injectCriticalStyle();

// Évite une double exécution si le script est chargé deux fois (défensif).
declare global {
  interface Window {
    __lmdrCommerceLoaded__?: boolean;
  }
}

async function boot(): Promise<void> {
  if (window.__lmdrCommerceLoaded__) return;
  window.__lmdrCommerceLoaded__ = true;

  try {
    // 1. Config : fetch remote config.json si inline SmootifyUserOptions
    // ne contient pas shopDomain + storefrontToken.
    await ensureConfigReady();
    void config.shopDomain;

    // 1b. Bootstrap locale : auto-detect navigator country/language au 1er
    // visit (selectMarketBasedOnBrowserLanguage default true → un Américain
    // verra USD/EN automatiquement). Persist dans localStorage. Sync cart
    // buyerIdentity. Toutes les queries Storefront qui suivront utiliseront
    // automatiquement le bon market via @inContext.
    void bootstrapLocale();

    // 1c. RGPD : attache le listener Shopify Customer Privacy (bridge vers
    //     smootify:consent_changed event) AVANT toute init analytics. Affiche
    //     la bannière cookie si requis (région GDPR/CCPA + pas de consent).
    //     Active Shopify Analytics native (events conditionnés à analyticsAllowed).
    attachConsentListener();
    // Google Consent Mode v2 : sync notre consent vers GTM/GA4/Ads (qui
    // peuvent être chargés via Webflow head). Tant que pas de consent →
    // ad_storage/analytics_storage = "denied" → aucun cookie Google posé.
    attachConsentMode();
    attachCookieBanner();
    attachAnalytics();

    // 2. Styles globaux (lmdr-hidden helper pour conditions).
    ensureHiddenStyle();

    // 3. Attach event listeners AVANT registerElements — l'upgrade des custom
    //    elements déclenche connectedCallback() synchrone qui dispatch des
    //    events (smootify:related_count, smootify:last-viewed_count, etc.).
    //    Si les listeners ne sont pas attachés à ce moment, les events sont
    //    perdus. Ordre critique.
    attachCartAutoRefresh();
    attachWishlistStorage();
    attachConditionAutoRefresh();
    attachWishlistBadge();

    // 4. Click handlers globaux (avant registerElements pour même raison).
    attachActionHandlers();
    attachCartTriggers();
    attachFreeShipping();
    attachCountdown();
    attachDraftCart();

    // NOTE : attachConditionAutoRefresh (étape 3) a déjà seedé les handles
    // wishlist/last-viewed depuis localStorage et appliqué une première passe
    // des conditions document-wide — pas besoin d'un applyConditionsInitial
    // séparé. registerElements peut maintenant déclencher les upgrades en
    // connaissant l'état global correct.

    // 5. Register custom elements — va upgrader les existantes + déclencher
    //    connectedCallback → loadMany → dispatch events captés par listeners.
    registerElements();

    // 5b. Branche le layer filter (inputs search/price + count + empty-state).
    //    Re-évalue automatiquement à chaque smootify:product_loaded pour prendre
    //    en compte les nouveaux clones hydratés.
    //
    //    CRITIQUE : sur une page filter avec 1536 clones, on reçoit 1536
    //    `smootify:product_loaded` events. Sans debounce, on appellerait
    //    refreshFilters() 1536 fois × 1536 clones itérés = ~2.4M opérations
    //    DOM = freeze du renderer 30+ secondes.
    //
    //    Avec un coalescing rAF : tous les events arrivés pendant le même
    //    frame déclenchent UNE SEULE refreshFilters() au prochain frame.
    refreshFilters(document);

    // Debounce + maxWait : sur la page filter on reçoit 1536 events
    // `smootify:product_loaded` qui peuvent arriver en cascade pendant 5-30s.
    //
    // - Trailing debounce 250ms : si plus aucun event pendant 250ms, refresh.
    // - MaxWait 1500ms : si les events arrivent en continu sans interruption,
    //   on force un refresh toutes les 1.5s pour ne pas attendre la fin.
    //
    // Sans maxWait : avec 1500 events arrivant à <250ms d'intervalle pendant
    // 30s, le debounce ne tire JAMAIS → applyFilters jamais appelé → user voit
    // les placeholders Webflow au lieu des produits hydratés.
    let trailingTimer: number | undefined;
    let maxWaitTimer: number | undefined;
    const fireRefresh = (): void => {
      if (trailingTimer !== undefined) {
        window.clearTimeout(trailingTimer);
        trailingTimer = undefined;
      }
      if (maxWaitTimer !== undefined) {
        window.clearTimeout(maxWaitTimer);
        maxWaitTimer = undefined;
      }
      try {
        refreshFilters(document);
        (window as unknown as { __lmdrRefreshCount?: number }).__lmdrRefreshCount =
          ((window as unknown as { __lmdrRefreshCount?: number }).__lmdrRefreshCount ?? 0) + 1;
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error("[lmdr] refreshFilters threw:", err);
      }
    };
    const scheduleRefresh = (): void => {
      if (trailingTimer !== undefined) window.clearTimeout(trailingTimer);
      trailingTimer = window.setTimeout(fireRefresh, 250);
      if (maxWaitTimer === undefined) {
        maxWaitTimer = window.setTimeout(fireRefresh, 1500);
      }
    };
    document.addEventListener("smootify:product_loaded", scheduleRefresh);

    // Safety nets multiples : refresh à T+2s, T+5s, T+10s, T+20s même sans event.
    // Couvre le cas où loadMany termine TARD (no events à T+2s) et aussi le
    // cas où aucun event ne déclenche scheduleRefresh.
    [2000, 5000, 10000, 20000].forEach((delay) => {
      window.setTimeout(() => fireRefresh(), delay);
    });

    // MutationObserver : détecte l'INSERTION de nouveaux clones dans le DOM
    // (par loadMany). Déclenche un refresh dès qu'un clone apparaît, sans
    // attendre les events smootify:product_loaded (qui arrivent plus tard).
    const observer = new MutationObserver((mutations) => {
      for (const m of mutations) {
        const addedNodes = Array.from(m.addedNodes);
        for (let i = 0; i < addedNodes.length; i++) {
          const n = addedNodes[i]!;
          if (
            n instanceof HTMLElement &&
            n.tagName === "SMOOTIFY-PRODUCT" &&
            n.hasAttribute("data-lmdr-multi-clone")
          ) {
            scheduleRefresh();
            return;
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // 5c. Predictive Search (autocomplete dropdown). Opt-in : s'active
    //    uniquement si la page contient un `<smootify-product data-id="search">`.
    attachPredictiveSearch();

    // 6. Public API window.Smootify.
    installSmootifyApi();

    // 7. Load cart initial (non-bloquant).
    void loadCart().catch((error: unknown) => {
      console.warn("[lmdr] cart restore failed:", error);
      emit("smootify:error", { type: "cart_load", message: "Impossible de charger le panier. Vérifiez votre connexion puis rechargez la page." });
    });

    // 8. Smootify-load scripts runner (sucre).
    setupSmootifyLoadScripts();

    // 9. Dispatch smootify:loaded (doc : emit sans detail).
    emit("smootify:loaded");
  } catch (err) {
    // eslint-disable-next-line no-console
    console.error("[lmdr-commerce] boot failed:", err);
    emit("smootify:error", {
      type: "boot",
      message: err instanceof Error ? err.message : String(err),
    });
  }
}

// Boot après DOMContentLoaded (ou immédiatement si déjà prêt).
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", () => void boot(), { once: true });
} else {
  void boot();
}
